import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, CheckCircle2, CalendarClock } from "lucide-react";
import { Reveal } from "./Reveal";
import { BOOKING_PATH } from "@/lib/site";
import { useLang } from "@/i18n/LanguageContext";
import { PACKAGE_NAMES } from "@/i18n/translations";
import { trackClick } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldClass =
  "w-full rounded-xl bg-[rgba(8,12,22,0.7)] border border-[var(--line)] px-4 py-3 text-[var(--white)] placeholder:text-[var(--muted-2)] font-sans text-sm outline-none transition-colors duration-300 focus:border-[var(--blue)] focus:ring-1 focus:ring-[var(--blue)]";

export const ContactSection = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ name: "", email: "", company: "", package: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setDone(false);
      setForm((f) => ({ ...f, package: e.detail }));
    };
    window.addEventListener("ph:select-package", handler);
    return () => window.removeEventListener("ph:select-package", handler);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error(c.errRequired);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        package: form.package || null,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success(c.successToast);
      setForm({ name: "", email: "", company: "", package: "", message: "" });
    } catch (err) {
      toast.error(c.errFail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="relative scroll-mt-24 py-24 lg:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid bg-grid-fade pointer-events-none" />
      <div className="relative z-10 mx-auto max-w-7xl px-5">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <Reveal>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-8 bg-[var(--blue)]" />
              <span className="eyebrow">{c.eyebrow}</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1] text-[var(--white)]" data-testid="contact-heading">
              {c.title}
            </h2>
            <p className="mt-5 text-base md:text-lg text-muted leading-relaxed" data-testid="contact-intro">{c.intro}</p>

            <div className="mt-8 glass-card rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-[rgba(61,123,255,0.1)] text-[var(--blue-bright)]">
                  <CalendarClock size={20} />
                </span>
                <div>
                  <div className="text-[var(--white)] font-medium text-sm" data-testid="contact-book-title">{c.bookCardTitle}</div>
                  <div className="text-xs text-muted" data-testid="contact-book-description">{c.bookCardSub}</div>
                </div>
              </div>
              <a href={`${BOOKING_PATH}?src=contact`} onClick={() => trackClick("book_time")} className="btn-ghost !py-2.5 !px-4 text-sm shrink-0" data-testid="contact-book-btn">
                {c.bookBtn} <ArrowUpRight size={15} />
              </a>
            </div>
          </Reveal>

          <Reveal>
            <div className="glass-card rounded-3xl p-6 lg:p-8">
              {done ? (
                <div className="text-center py-10" data-testid="contact-success">
                  <div className="mx-auto grid place-items-center h-16 w-16 rounded-2xl bg-[rgba(54,226,160,0.12)] text-[var(--green)] mb-5">
                    <CheckCircle2 size={30} />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-[var(--white)]">{c.successTitle}</h3>
                  <p className="mt-2 text-sm text-muted">{c.successSub}</p>
                  <button onClick={() => setDone(false)} className="btn-ghost mt-6 mx-auto" data-testid="contact-reset-btn">
                    {c.sendAnother}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-muted mb-1.5 font-mono uppercase tracking-wider">{c.labels.name} *</label>
                      <input className={fieldClass} value={form.name} onChange={update("name")} placeholder={c.placeholders.name} data-testid="contact-name-input" />
                    </div>
                    <div>
                      <label className="block text-xs text-muted mb-1.5 font-mono uppercase tracking-wider">{c.labels.email} *</label>
                      <input type="email" className={fieldClass} value={form.email} onChange={update("email")} placeholder={c.placeholders.email} data-testid="contact-email-input" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-muted mb-1.5 font-mono uppercase tracking-wider">{c.labels.company}</label>
                      <input className={fieldClass} value={form.company} onChange={update("company")} placeholder={c.placeholders.company} data-testid="contact-company-input" />
                    </div>
                    <div>
                      <label className="block text-xs text-muted mb-1.5 font-mono uppercase tracking-wider">{c.labels.pkg}</label>
                      <select className={`${fieldClass} appearance-none cursor-pointer`} value={form.package} onChange={update("package")} data-testid="contact-package-select">
                        <option value="">{c.selectDefault}</option>
                        {PACKAGE_NAMES.map((p) => (
                          <option key={p} value={p} className="bg-[#0b111e]">{p}</option>
                        ))}
                        <option value={c.otherOption} className="bg-[#0b111e]">{c.otherOption}</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-muted mb-1.5 font-mono uppercase tracking-wider">{c.labels.message} *</label>
                    <textarea rows={4} className={`${fieldClass} resize-none`} value={form.message} onChange={update("message")} placeholder={c.placeholders.message} data-testid="contact-message-input" />
                  </div>
                  <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed" data-testid="contact-submit-btn">
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> {c.sending}
                      </>
                    ) : (
                      <>
                        {c.submit} <ArrowUpRight size={18} />
                      </>
                    )}
                  </button>
                  <p className="text-center text-[11px] text-[var(--muted-2)] font-mono">{c.footnote}</p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
